/**
 * Cluster Detail Panel
 *
 * Always-visible panel showing cluster information.
 * Supports two modes:
 * - Hover preview: Shows cluster details while hovering (temporary)
 * - Selected: Shows cluster details persistently after clicking
 *
 * Features:
 * - Cluster title and AI-generated description
 * - Outcome distribution with stacked bar visualization
 * - Key statistics (avg award, decision count)
 * - Scrollable list of decisions within the cluster
 * - Risk indicator for high upheld rate clusters
 * - Skeleton state when no cluster is active
 */

import React from 'react';
import { cn } from '../../../lib/utils';
import { Badge } from '../../ui/badge';
import { ScrollArea } from '../../ui/scroll-area';
import { Skeleton } from '../../ui/skeleton';
import { FOSDecisionCard } from './FOSDecisionCard';
import IconWrapper from '../../IconWrapper';
import { FiAlertTriangle, FiMousePointer, FiLayers } from 'react-icons/fi';
import type { FOSClusterInfo, FOSDecisionCard as FOSDecisionCardData } from '../../../utils/api';

interface ClusterDetailPanelProps {
  /** The currently hovered cluster (preview mode) */
  hoveredCluster: FOSClusterInfo | null;
  /** The selected cluster (persistent mode) */
  selectedCluster: FOSClusterInfo | null;
  /** Decisions within this cluster (fetched separately) */
  decisions: FOSDecisionCardData[];
  /** Loading state for decisions */
  isLoading: boolean;
  /** Callback when a decision is clicked */
  onDecisionSelect: (decisionId: string) => void;
  /** Callback to clear selection */
  onClearSelection: () => void;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Calculate the upheld ratio for risk assessment
 */
function calculateUpheldRatio(distribution: Record<string, number> | null): number {
  if (!distribution) return 0;
  const upheld = distribution.upheld || 0;
  const total = Object.values(distribution).reduce((a, b) => a + b, 0);
  return total > 0 ? upheld / total : 0;
}

/**
 * Outcome distribution bar component
 */
const OutcomeDistributionBar: React.FC<{
  distribution: Record<string, number> | null;
}> = ({ distribution }) => {
  if (!distribution) return null;

  const total = Object.values(distribution).reduce((a, b) => a + b, 0);
  if (total === 0) return null;

  const upheld = distribution.upheld || 0;
  const partial = distribution.partially_upheld || 0;
  const notUpheld = distribution.not_upheld || 0;

  return (
    <div className="space-y-2">
      {/* Stacked bar */}
      <div className="h-2.5 rounded-full bg-muted overflow-hidden flex">
        {upheld > 0 && (
          <div
            className="h-full bg-berry transition-all duration-300"
            style={{ width: `${(upheld / total) * 100}%` }}
          />
        )}
        {partial > 0 && (
          <div
            className="h-full bg-purple transition-all duration-300"
            style={{ width: `${(partial / total) * 100}%` }}
          />
        )}
        {notUpheld > 0 && (
          <div
            className="h-full bg-teal transition-all duration-300"
            style={{ width: `${(notUpheld / total) * 100}%` }}
          />
        )}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
        {upheld > 0 && (
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-berry" />
            <span className="text-muted-foreground">{upheld}</span>
            <span className="text-xs text-muted-foreground/70">Upheld</span>
          </span>
        )}
        {partial > 0 && (
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-purple" />
            <span className="text-muted-foreground">{partial}</span>
            <span className="text-xs text-muted-foreground/70">Partial</span>
          </span>
        )}
        {notUpheld > 0 && (
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-teal" />
            <span className="text-muted-foreground">{notUpheld}</span>
            <span className="text-xs text-muted-foreground/70">Not Upheld</span>
          </span>
        )}
      </div>
    </div>
  );
};

/**
 * Loading skeleton for the decisions list
 */
const DecisionListSkeleton: React.FC = () => (
  <div className="space-y-2">
    {[1, 2, 3, 4].map((i) => (
      <div key={i} className="p-3 rounded-lg border border-border/50 bg-card">
        <div className="flex items-start justify-between mb-2">
          <Skeleton className="h-3 w-24" />
          <Skeleton className="h-4 w-4 rounded-full" />
        </div>
        <Skeleton className="h-3 w-full mb-1" />
        <Skeleton className="h-3 w-3/4" />
      </div>
    ))}
  </div>
);

/**
 * Empty state skeleton for the panel
 */
const EmptyStateSkeleton: React.FC = () => (
  <div className="p-4 space-y-5">
    {/* Header placeholder */}
    <div className="flex flex-col items-center justify-center py-8 text-center">
      <div className="w-12 h-12 rounded-full bg-muted/50 flex items-center justify-center mb-3">
        <IconWrapper icon={FiLayers} className="h-5 w-5 text-muted-foreground/50" />
      </div>
      <p className="text-sm font-medium text-muted-foreground mb-1">No Cluster Selected</p>
      <p className="text-xs text-muted-foreground/70 max-w-[180px]">
        Hover over a cluster in the visualization or sidebar to preview details
      </p>
    </div>

    {/* Skeleton structure */}
    <div className="space-y-4 opacity-30">
      <div className="space-y-2">
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-5/6" />
      </div>
      <Skeleton className="h-2.5 w-full rounded-full" />
      <div className="grid grid-cols-2 gap-2">
        <Skeleton className="h-16 rounded-lg" />
        <Skeleton className="h-16 rounded-lg" />
      </div>
    </div>
  </div>
);

export const ClusterDetailPanel: React.FC<ClusterDetailPanelProps> = ({
  hoveredCluster,
  selectedCluster,
  decisions,
  isLoading,
  onDecisionSelect,
  onClearSelection,
  className,
}) => {
  // Show hovered cluster as preview, fall back to selected cluster
  const activeCluster = hoveredCluster || selectedCluster;
  const isPreviewMode = hoveredCluster !== null && selectedCluster?.cluster_num !== hoveredCluster.cluster_num;
  const isSelected = selectedCluster !== null && activeCluster?.cluster_num === selectedCluster.cluster_num;

  const upheldRatio = activeCluster ? calculateUpheldRatio(activeCluster.outcome_distribution) : 0;
  const upheldPercent = Math.round(upheldRatio * 100);
  const isHighRisk = upheldRatio >= 0.6; // 60%+ upheld = high risk

  return (
    <div
      className={cn(
        "w-80 border-l border-border/60 bg-card flex flex-col h-full",
        className
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border/40">
        <div className="flex items-center gap-2">
          <IconWrapper icon={FiLayers} className="h-4 w-4 text-muted-foreground" />
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Cluster Details</span>
        </div>
        {isPreviewMode && (
          <span className="flex items-center gap-1 text-xs text-muted-foreground/70 bg-muted/50 px-2 py-0.5 rounded-full">
            <IconWrapper icon={FiMousePointer} className="h-3 w-3" />
            Preview
          </span>
        )}
        {isSelected && !isPreviewMode && (
          <button
            onClick={onClearSelection}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            Clear
          </button>
        )}
      </div>

      {!activeCluster ? (
        <EmptyStateSkeleton />
      ) : (
        <ScrollArea className="flex-1">
          <div className="p-4 space-y-5">
            {/* Cluster Title */}
            <div>
              <h2 className="text-lg font-semibold text-foreground leading-tight">
                {activeCluster.label || `Cluster ${activeCluster.cluster_num}`}
              </h2>
              {activeCluster.description && (
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                  {activeCluster.description}
                </p>
              )}
            </div>

            {/* Risk Indicator */}
            {isHighRisk && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-berry/5 border border-berry/20">
                <IconWrapper icon={FiAlertTriangle} className="h-4 w-4 text-berry flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-berry">High Upheld Rate</p>
                  <p className="text-xs text-berry/70">
                    {upheldPercent}% of decisions in this cluster were upheld
                  </p>
                </div>
              </div>
            )}

            {/* Outcome Distribution */}
            <div className="space-y-3">
              <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Outcomes
              </h3>
              <OutcomeDistributionBar distribution={activeCluster.outcome_distribution} />
            </div>

            {/* Key Stats */}
            <div className="space-y-3">
              <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Key Stats
              </h3>
              <div className="grid grid-cols-2 gap-2">
                <div className="p-3 rounded-lg bg-muted/30 border border-border/40">
                  <div className="text-xs text-muted-foreground mb-0.5">Avg Award</div>
                  <div className="text-lg font-semibold text-emerald-600 tabular-nums">
                    {activeCluster.avg_compensation
                      ? `£${Math.round(activeCluster.avg_compensation).toLocaleString()}`
                      : 'N/A'}
                  </div>
                </div>
                <div className="p-3 rounded-lg bg-muted/30 border border-border/40">
                  <div className="text-xs text-muted-foreground mb-0.5">Decisions</div>
                  <div className="text-lg font-semibold tabular-nums">
                    {activeCluster.decision_count.toLocaleString()}
                  </div>
                </div>
              </div>

              {/* Top Products */}
              {activeCluster.top_products && activeCluster.top_products.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {activeCluster.top_products.slice(0, 4).map((product) => (
                    <Badge key={product} variant="secondary" className="text-xs">
                      {product}
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Decisions List - only show when selected (not preview) */}
            {isSelected && !isPreviewMode && (
              <div className="space-y-3">
                <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Decisions ({decisions.length || activeCluster.decision_count})
                </h3>

                {isLoading ? (
                  <DecisionListSkeleton />
                ) : decisions.length > 0 ? (
                  <div className="space-y-2">
                    {decisions.map((decision) => (
                      <FOSDecisionCard
                        key={decision.id}
                        decision={decision}
                        variant="compact"
                        showCompany={false}
                        showDate={false}
                        showSimilarityScore={false}
                        onClick={() => onDecisionSelect(decision.id)}
                      />
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4 bg-muted/30 rounded-lg">
                    Click to select and load decisions
                  </p>
                )}
              </div>
            )}

            {/* Call to action when in preview mode */}
            {isPreviewMode && (
              <div className="text-center py-3 border-t border-border/40">
                <p className="text-xs text-muted-foreground">
                  Click cluster to select and view decisions
                </p>
              </div>
            )}
          </div>
        </ScrollArea>
      )}
    </div>
  );
};

export default ClusterDetailPanel;
